import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Image, 
  MessageSquare, 
  FileText, 
  DollarSign, 
  LogOut, 
  Loader2, 
  Trash2, 
  Eye, 
  Check, 
  X,
  Plus,
  LayoutDashboard,
  ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to load and display quote images
const QuoteImage = ({ imageId }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await axios.get(`${API}/images/${imageId}`);
        if (response.data.image_base64) {
          setImageSrc(`data:image/jpeg;base64,${response.data.image_base64}`);
        }
      } catch (err) {
        console.error("Failed to load image:", err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (imageId) {
      fetchImage();
    } else {
      setIsLoading(false);
      setError(true);
    }
  }, [imageId]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white/5">
        <Loader2 className="w-6 h-6 text-copper animate-spin" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white/5">
        <ImageIcon className="w-8 h-8 text-gray-500" />
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt="Quote submission"
      className="w-full h-full object-cover"
    />
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Login state
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Dashboard state
  const [stats, setStats] = useState(null);
  
  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [newArtwork, setNewArtwork] = useState({
    title: "",
    description: "",
    image_url: "",
    size: "8x10",
    category: "Custom"
  });
  const [isAddingArtwork, setIsAddingArtwork] = useState(false);
  
  // Quotes state
  const [quotes, setQuotes] = useState([]);
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  
  // Pricing state
  const [pricing, setPricing] = useState({
    size_5x7: 20,
    size_8x10: 35,
    size_12x16: 50,
    portrait: 85
  });
  const [isSavingPricing, setIsSavingPricing] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("kairos_admin_token");
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      loadDashboardData();
    } catch (error) {
      localStorage.removeItem("kairos_admin_token");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem("kairos_admin_token", response.data.token);
      setIsAuthenticated(true);
      toast.success("Login successful!");
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("kairos_admin_token");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("kairos_admin_token")}` }
  });

  const loadDashboardData = async () => {
    try {
      const [statsRes, galleryRes, quotesRes, contactsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/admin/quotes`, getAuthHeaders()),
        axios.get(`${API}/admin/contacts`, getAuthHeaders()),
        axios.get(`${API}/pricing`)
      ]);
      
      setStats(statsRes.data);
      setGallery(galleryRes.data);
      setQuotes(quotesRes.data);
      setContacts(contactsRes.data);
      
      // Set pricing from API
      const sizes = pricingRes.data.sizes;
      setPricing({
        size_5x7: sizes.find(s => s.size === "5x7")?.price || 20,
        size_8x10: sizes.find(s => s.size === "8x10")?.price || 35,
        size_12x16: sizes.find(s => s.size === "12x16")?.price || 50,
        portrait: pricingRes.data.portrait?.starting_price || 85
      });
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    }
  };

  // Gallery functions
  const handleAddArtwork = async (e) => {
    e.preventDefault();
    if (!newArtwork.title || !newArtwork.image_url) {
      toast.error("Please fill in title and image URL");
      return;
    }

    setIsAddingArtwork(true);
    try {
      await axios.post(`${API}/admin/gallery`, newArtwork, getAuthHeaders());
      toast.success("Artwork added successfully!");
      setNewArtwork({ title: "", description: "", image_url: "", size: "8x10", category: "Custom" });
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to add artwork");
    } finally {
      setIsAddingArtwork(false);
    }
  };

  const handleDeleteArtwork = async (id) => {
    if (!window.confirm("Are you sure you want to delete this artwork?")) return;
    
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, getAuthHeaders());
      toast.success("Artwork deleted");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to delete artwork");
    }
  };

  // Quote functions
  const handleUpdateQuoteStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, getAuthHeaders());
      toast.success(`Quote marked as ${status}`);
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to update quote");
    }
  };

  const handleDeleteQuote = async (id) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, getAuthHeaders());
      toast.success("Quote deleted");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };

  // Contact functions
  const handleMarkContactRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}/read`, {}, getAuthHeaders());
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, getAuthHeaders());
      toast.success("Message deleted");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  // Pricing functions
  const handleSavePricing = async () => {
    setIsSavingPricing(true);
    try {
      await axios.put(`${API}/admin/pricing`, pricing, getAuthHeaders());
      toast.success("Pricing updated successfully!");
    } catch (error) {
      toast.error("Failed to update pricing");
    } finally {
      setIsSavingPricing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-copper animate-spin" />
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl copper-text mb-2">Admin Login</h1>
            <p className="text-gray-400 font-body text-sm">Kairos Copper Art Dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-white/[0.02] border border-white/5 p-8">
            <div className="space-y-6">
              <div>
                <Label className="text-gray-400 font-body text-sm">Email</Label>
                <Input
                  type="email"
                  data-testid="admin-email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  placeholder="Enter your email"
                  className="mt-2 bg-transparent border-white/20 focus:border-copper text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400 font-body text-sm">Password</Label>
                <Input
                  type="password"
                  data-testid="admin-password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Enter your password"
                  className="mt-2 bg-transparent border-white/20 focus:border-copper text-white"
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={isLoggingIn}
                className="w-full bg-copper hover:bg-copper-light text-white font-body tracking-widest uppercase py-6"
              >
                {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
              </Button>
            </div>
          </form>
          
          <p className="text-center mt-6">
            <a href="/" className="text-gray-500 hover:text-copper text-sm font-body transition-colors">
              ← Back to Website
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl copper-text">Admin Dashboard</h1>
            <p className="text-gray-400 font-body text-sm">Manage your Kairos Copper Art website</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            variant="outline"
            className="border-white/20 text-gray-400 hover:border-copper hover:text-copper"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
          {[
            { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
            { id: "gallery", label: "Gallery", icon: Image },
            { id: "contacts", label: "Messages", icon: MessageSquare, badge: stats?.unread_contacts },
            { id: "pricing", label: "Pricing", icon: DollarSign }
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 font-body text-sm transition-colors ${
                activeTab === tab.id
                  ? "text-copper border-b-2 border-copper"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
              {tab.badge > 0 && (
                <span className="bg-copper text-white text-xs px-2 py-0.5 rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && stats && (
          <div data-testid="dashboard-content" className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/[0.02] border border-white/5 p-6">
              <p className="text-gray-400 font-body text-sm mb-2">Gallery Items</p>
              <p className="font-heading text-3xl copper-text">{stats.total_artworks}</p>
            </div>
            <div className="bg-white/[0.02] border border-white/5 p-6">
              <p className="text-gray-400 font-body text-sm mb-2">Total Quotes</p>
              <p className="font-heading text-3xl copper-text">{stats.total_quotes}</p>
            </div>
            <div className="bg-white/[0.02] border border-white/5 p-6">
              <p className="text-gray-400 font-body text-sm mb-2">Pending Quotes</p>
              <p className="font-heading text-3xl text-yellow-500">{stats.pending_quotes}</p>
            </div>
            <div className="bg-white/[0.02] border border-white/5 p-6">
              <p className="text-gray-400 font-body text-sm mb-2">Unread Messages</p>
              <p className="font-heading text-3xl text-yellow-500">{stats.unread_contacts}</p>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === "gallery" && (
          <div data-testid="gallery-content">
            {/* Add New Artwork Form */}
            <div className="bg-white/[0.02] border border-white/5 p-6 mb-8">
              <h2 className="font-heading text-xl mb-4 text-white flex items-center gap-2">
                <Plus size={20} className="text-copper" />
                Add New Artwork
              </h2>
              <form onSubmit={handleAddArtwork} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm">Title *</Label>
                  <Input
                    data-testid="artwork-title"
                    value={newArtwork.title}
                    onChange={(e) => setNewArtwork({ ...newArtwork, title: e.target.value })}
                    placeholder="Artwork title"
                    className="mt-1 bg-transparent border-white/20 focus:border-copper text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Image URL *</Label>
                  <Input
                    data-testid="artwork-url"
                    value={newArtwork.image_url}
                    onChange={(e) => setNewArtwork({ ...newArtwork, image_url: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 bg-transparent border-white/20 focus:border-copper text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Size</Label>
                  <select
                    data-testid="artwork-size"
                    value={newArtwork.size}
                    onChange={(e) => setNewArtwork({ ...newArtwork, size: e.target.value })}
                    className="mt-1 w-full bg-transparent border border-white/20 focus:border-copper text-white p-2"
                  >
                    <option value="5x7" className="bg-[#050505]">5×7"</option>
                    <option value="8x10" className="bg-[#050505]">8×10"</option>
                    <option value="12x16" className="bg-[#050505]">12×16"</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Category</Label>
                  <select
                    data-testid="artwork-category"
                    value={newArtwork.category}
                    onChange={(e) => setNewArtwork({ ...newArtwork, category: e.target.value })}
                    className="mt-1 w-full bg-transparent border border-white/20 focus:border-copper text-white p-2"
                  >
                    <option value="Custom" className="bg-[#050505]">Custom</option>
                    <option value="Abstract" className="bg-[#050505]">Abstract</option>
                    <option value="Portrait" className="bg-[#050505]">Portrait</option>
                    <option value="Featured" className="bg-[#050505]">Featured</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-400 text-sm">Description</Label>
                  <Textarea
                    data-testid="artwork-description"
                    value={newArtwork.description}
                    onChange={(e) => setNewArtwork({ ...newArtwork, description: e.target.value })}
                    placeholder="Brief description..."
                    className="mt-1 bg-transparent border-white/20 focus:border-copper text-white"
                    rows={2}
                  />
                </div>
                <div className="md:col-span-2">
                  <Button
                    type="submit"
                    data-testid="add-artwork-btn"
                    disabled={isAddingArtwork}
                    className="bg-copper hover:bg-copper-light text-white"
                  >
                    {isAddingArtwork ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                    Add Artwork
                  </Button>
                </div>
              </form>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((item) => (
                <div key={item.id} className="bg-white/[0.02] border border-white/5 overflow-hidden group">
                  <div className="aspect-square relative">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1601069151015-0a7fb104503c?w=300&fit=crop";
                      }}
                    />
                    <button
                      onClick={() => handleDeleteArtwork(item.id)}
                      className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-500 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="p-3">
                    <p className="text-white font-body text-sm truncate">{item.title}</p>
                    <p className="text-gray-500 text-xs">{item.category} • {item.size}</p>
                  </div>
                </div>
              ))}
            </div>

            {gallery.length === 0 && (
              <p className="text-center text-gray-500 py-12">No artwork in gallery yet</p>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <div data-testid="quotes-content" className="space-y-4">
            {quotes.map((quote) => (
              <div key={quote.id} className="bg-white/[0.02] border border-white/5 p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Image Preview */}
                  {quote.image_id && (
                    <div className="w-full md:w-48 h-48 flex-shrink-0 border border-white/10 overflow-hidden">
                      <QuoteImage imageId={quote.image_id} />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading text-lg text-white">{quote.customer_name}</h3>
                          <span className={`text-xs px-2 py-1 ${
                            quote.status === "pending" ? "bg-yellow-500/20 text-yellow-500" :
                            quote.status === "accepted" ? "bg-green-500/20 text-green-500" :
                            quote.status === "declined" ? "bg-red-500/20 text-red-500" :
                            "bg-blue-500/20 text-blue-500"
                          }`}>
                            {quote.status}
                          </span>
                        </div>
                        <p className="text-gray-400 font-body text-sm">{quote.customer_email}</p>
                        {quote.customer_phone && (
                          <p className="text-gray-500 font-body text-sm">{quote.customer_phone}</p>
                        )}
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Size</p>
                            <p className="text-white">{quote.selected_size}"</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Complexity</p>
                            <p className="text-white">{quote.complexity_score}/10</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Portrait</p>
                            <p className="text-white">{quote.is_portrait ? "Yes" : "No"}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Price</p>
                            <p className="text-copper font-heading text-lg">${quote.final_price?.toFixed(2)}</p>
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs mt-2">{quote.analysis_notes}</p>
                        <p className="text-gray-600 text-xs mt-2">
                          {new Date(quote.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {quote.status === "pending" && (
                          <>
                            <Button
                              onClick={() => handleUpdateQuoteStatus(quote.id, "accepted")}
                              className="bg-green-600 hover:bg-green-500 text-white text-xs"
                              size="sm"
                            >
                              <Check size={14} className="mr-1" /> Accept
                            </Button>
                            <Button
                              onClick={() => handleUpdateQuoteStatus(quote.id, "declined")}
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-xs"
                              size="sm"
                            >
                              <X size={14} className="mr-1" /> Decline
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={() => handleDeleteQuote(quote.id)}
                          variant="ghost"
                          className="text-gray-500 hover:text-red-500"
                          size="sm"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {quotes.length === 0 && (
              <p className="text-center text-gray-500 py-12">No quote requests yet</p>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div data-testid="contacts-content" className="space-y-4">
            {contacts.map((contact) => (
              <div 
                key={contact.id} 
                className={`bg-white/[0.02] border p-6 ${
                  contact.read ? "border-white/5" : "border-copper/30"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-lg text-white">{contact.name}</h3>
                      {!contact.read && (
                        <span className="text-xs bg-copper/20 text-copper px-2 py-1">New</span>
                      )}
                    </div>
                    <p className="text-gray-400 font-body text-sm">{contact.email}</p>
                    {contact.phone && (
                      <p className="text-gray-500 font-body text-sm">{contact.phone}</p>
                    )}
                    <p className="text-gray-300 font-body text-sm mt-3 whitespace-pre-wrap">
                      {contact.message}
                    </p>
                    <p className="text-gray-600 text-xs mt-2">
                      {new Date(contact.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!contact.read && (
                      <Button
                        onClick={() => handleMarkContactRead(contact.id)}
                        variant="outline"
                        className="border-copper text-copper hover:bg-copper hover:text-white text-xs"
                        size="sm"
                      >
                        <Eye size={14} className="mr-1" /> Mark Read
                      </Button>
                    )}
                    <Button
                      onClick={() => handleDeleteContact(contact.id)}
                      variant="ghost"
                      className="text-gray-500 hover:text-red-500"
                      size="sm"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {contacts.length === 0 && (
              <p className="text-center text-gray-500 py-12">No messages yet</p>
            )}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === "pricing" && (
          <div data-testid="pricing-content" className="max-w-md">
            <div className="bg-white/[0.02] border border-white/5 p-6">
              <h2 className="font-heading text-xl mb-6 text-white">Update Pricing</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-400 text-sm">5×7" Price ($)</Label>
                  <Input
                    type="number"
                    data-testid="price-5x7"
                    value={pricing.size_5x7}
                    onChange={(e) => setPricing({ ...pricing, size_5x7: parseFloat(e.target.value) })}
                    className="mt-1 bg-transparent border-white/20 focus:border-copper text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">8×10" Price ($)</Label>
                  <Input
                    type="number"
                    data-testid="price-8x10"
                    value={pricing.size_8x10}
                    onChange={(e) => setPricing({ ...pricing, size_8x10: parseFloat(e.target.value) })}
                    className="mt-1 bg-transparent border-white/20 focus:border-copper text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">12×16" Price ($)</Label>
                  <Input
                    type="number"
                    data-testid="price-12x16"
                    value={pricing.size_12x16}
                    onChange={(e) => setPricing({ ...pricing, size_12x16: parseFloat(e.target.value) })}
                    className="mt-1 bg-transparent border-white/20 focus:border-copper text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Portrait Starting Price ($)</Label>
                  <Input
                    type="number"
                    data-testid="price-portrait"
                    value={pricing.portrait}
                    onChange={(e) => setPricing({ ...pricing, portrait: parseFloat(e.target.value) })}
                    className="mt-1 bg-transparent border-white/20 focus:border-copper text-white"
                  />
                </div>
                <Button
                  onClick={handleSavePricing}
                  data-testid="save-pricing-btn"
                  disabled={isSavingPricing}
                  className="w-full bg-copper hover:bg-copper-light text-white mt-4"
                >
                  {isSavingPricing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Pricing
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
